import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  Form,
  Input,
  MessagePlugin,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  type PrimaryTableCol,
} from 'tdesign-react';
import {
  Plus, Pencil, Trash2, Search, X,
  Mail, Phone, MapPin, Building2, UserCircle,
} from 'lucide-react';
import { eCardApi, tenantAdminBusinessCardApi, tokenStorage } from '../../api/index';
import { ColorPickerField } from './components/CommonComponents';
import ProductImageUploader from '@/user/components/ProductImageUploader';
import ProductRichTextEditor from '@/user/components/ProductRichTextEditor';

const { FormItem } = Form;
const { Option } = Select;

interface BusinessCardFormState {
  id?: number;
  userId?: number;
  name: string;
  phone: string;
  position: string;
  email: string;
  company: string;
  address: string;
  status: number;
  /** 名片字体颜色 */
  fontColor: string;
  /** 名片背景图地址 */
  backgroundImage: string;
  /** 个人简介（富文本，列表不展示） */
  introduction: string;
}

const emptyForm: BusinessCardFormState = {
  name: '',
  phone: '',
  position: '',
  email: '',
  company: '',
  address: '',
  status: 1,
  fontColor: '#000000',
  backgroundImage: '',
  introduction: '',
};

const normalizeStatus = (value: unknown): 0 | 1 => Number(value) === 1 ? 1 : 0;

/** 富文本为空时（Quill 默认的 <p><br></p>）统一提交空串 */
const normalizeRichText = (html?: string) => {
  const value = (html || '').trim();
  if (!value || /^<p>(<br\s*\/?>|\s|&nbsp;)*<\/p>$/.test(value)) return '';
  return value;
};

// 员工搜索选择弹框使用的数据
interface PersonnelOption {
  userId: number;
  name: string;
  phone: string;
  position?: string;
  email?: string;
  company?: string;
  address?: string;
}

export default function UserEcardManage() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState<number | undefined>(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any | null>(null);
  const [formData, setFormData] = useState<BusinessCardFormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<any>(null);

  // 员工选择相关状态
  const [userPickerVisible, setUserPickerVisible] = useState(false);
  const [userSearchPhone, setUserSearchPhone] = useState('');
  const [userList, setUserList] = useState<PersonnelOption[]>([]);
  const [userListLoading, setUserListLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PersonnelOption | null>(null);
  const initializedRef = useRef(false);
  const paginationRef = useRef(pagination);
  const filtersRef = useRef({ keyword, status });

  paginationRef.current = pagination;
  filtersRef.current = { keyword, status };

  const fetchData = async (params: { pageNum?: number; pageSize?: number; keyword?: string; status?: number } = {}) => {
    setLoading(true);
    try {
      const res = await tenantAdminBusinessCardApi.getPageList({
        pageNum: params.pageNum ?? paginationRef.current.current,
        pageSize: params.pageSize ?? paginationRef.current.pageSize,
        keyword: params.keyword ?? filtersRef.current.keyword ?? undefined,
        status: params.status ?? filtersRef.current.status ?? undefined,
      });
      if (res.code === 200) {
        setData(res.data.list || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.total || 0,
          ...(params.pageNum ? { current: params.pageNum } : null),
          ...(params.pageSize ? { pageSize: params.pageSize } : null),
        }));
      } else {
        MessagePlugin.error(res.message || '加载列表失败');
      }
    } catch {
      MessagePlugin.error('加载列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ pageNum: 1, keyword });
  };

  const handleReset = () => {
    setKeyword('');
    setStatus(undefined);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ pageNum: 1, keyword: '', status: undefined });
  };

  const handlePageChange = (pageInfo: { current: number; pageSize: number }) => {
    setPagination((prev) => ({ ...prev, current: pageInfo.current, pageSize: pageInfo.pageSize }));
    fetchData({ pageNum: pageInfo.current, pageSize: pageInfo.pageSize });
  };

  // 打开员工选择器搜索员工
  const openUserPicker = () => {
    setUserSearchPhone(formData.phone || '');
    setUserList([]);
    setSelectedUser(null);
    setUserPickerVisible(true);
  };

  // 根据手机号搜索员工
  const doSearchUsers = async () => {
    if (!userSearchPhone.trim()) {
      MessagePlugin.warning('请输入手机号搜索员工');
      return;
    }
    setUserListLoading(true);
    try {
      const res = await eCardApi.getPageList({
        pageNum: 1,
        pageSize: 50,
        phone: userSearchPhone.trim(),
      });
      if (res.code === 200) {
        const list = (res.data.list || []).map((u: any) => ({
          userId: u.userId ?? u.id,
          name: u.name || u.nickname || '-',
          phone: u.phone || '-',
          position: u.position,
          email: u.cardEmail || u.email,
          company: u.company,
          address: u.address,
        }));
        setUserList(list);
        if (list.length === 0) {
          MessagePlugin.warning('未找到该手机号对应的员工');
        }
      } else {
        MessagePlugin.error(res.message || '搜索员工失败');
      }
    } catch {
      MessagePlugin.error('搜索员工失败');
    } finally {
      setUserListLoading(false);
    }
  };
   const handleStatusChange = async (record: any, checked: boolean) => {
      try {
        let params = {
          status: checked ? 1 : 0,
        }
        await tenantAdminBusinessCardApi.update(record.id, params)
        MessagePlugin.success('状态更新成功');
        fetchData();
      } catch (error) {
        MessagePlugin.error('状态更新失败');
      }
    };

  // 选择员工并回填信息
  const confirmSelectUser = async (user: PersonnelOption) => {
    let selected = user;

    try {
      const res = await eCardApi.getById(user.userId);
      if (res.code === 200 && res.data) {
        const detail = res.data;
        const card = detail.card || {};
        const employee = detail.user || {};
        selected = {
          ...user,
          name: card.name || (employee as any)?.name || user.name,
          phone: card.phone || employee.phone || user.phone,
          position: card.position || user.position,
          email: card.email || employee.email || user.email,
          company: card.company || user.company,
          address: card.address || user.address,
        };
      }
    } catch {
      // 详情获取失败时，仍使用搜索结果中的基础信息回填
    }

    const nextFormData = {
      userId: selected.userId,
      name: selected.name === '-' ? '' : selected.name,
      phone: selected.phone === '-' ? '' : selected.phone,
      position: selected.position || '',
      email: selected.email || '',
      company: selected.company || '',
      address: selected.address || '',
    };
    setSelectedUser(selected);
    setFormData((prev) => ({ ...prev, ...nextFormData }));
    formRef.current?.setFieldsValue?.(nextFormData);
    setUserPickerVisible(false);
  };

  const openCreate = () => {
    setEditingRecord(null);
    setFormData(emptyForm);
    setSelectedUser(null);
    setDialogVisible(true);
  };

  const openEdit = async (record: any) => {
    try {
      // 列表行不一定带回个人简介等字段，这里补拉一次详情用于回显
      let detail = record;
      try {
        const res = await tenantAdminBusinessCardApi.getById(record.id);
        if (res.code === 200 && res.data) {
          detail = { ...record, ...(res.data.card || res.data) };
        }
      } catch {
        // 详情获取失败时用列表行数据兜底
      }

      setEditingRecord(detail);
      setFormData({
          id: detail.id,
          userId: detail.userId,
          name: detail.name || '',
          phone: detail.phone || '',
          position: detail.position || '',
          email: detail.email || '',
          company: detail.company || '',
          address: detail.address || '',
          status: normalizeStatus(detail.status ?? 1),
          fontColor: detail.fontColor || '#000000',
          backgroundImage: detail.backgroundImage || '',
          introduction: detail.introduction || '',
        });
        setSelectedUser({
          userId: detail.userId,
          name: detail.name || '',
          phone: detail.phone || '',
          position: detail.position,
          email: detail.email,
          company: detail.company,
          address: detail.address,
        });
        setDialogVisible(true);

        // 编辑弹窗复用时会保留上一次的表单值，这里显式回填一次，保证个人简介等字段正确回显
        const backfill = { introduction: detail.introduction || '' };
        requestAnimationFrame(() => {
          formRef.current?.setFieldsValue?.(backfill);
        });
    } catch {
      MessagePlugin.error('获取详情失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await tenantAdminBusinessCardApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        setSelectedRowKeys((prev) => prev.filter((key) => key !== id));
        fetchData();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch {
      MessagePlugin.error('删除失败');
    }
  };

  const handleDisable = async (id: number) => {
    try {
      const res = await tenantAdminBusinessCardApi.disable(id);
      if (res.code === 200) {
        MessagePlugin.success('停用成功');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '停用失败');
      }
    } catch {
      MessagePlugin.error('停用失败');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      MessagePlugin.warning('请选择要删除的数据');
      return;
    }
    try {
      const res = await tenantAdminBusinessCardApi.deleteBatch(selectedRowKeys);
      if (res.code === 200) {
        MessagePlugin.success('批量删除成功');
        setSelectedRowKeys([]);
        fetchData();
      } else {
        MessagePlugin.error(res.message || '批量删除失败');
      }
    } catch {
      MessagePlugin.error('批量删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const result = await formRef.current?.validate?.();
      if (result !== true) return;

      // 必填字段校验
      if (!formData.name?.trim()) { MessagePlugin.warning('请输入姓名'); return; }
      if (!formData.phone?.trim()) { MessagePlugin.warning('请输入手机号'); return; }
      if (!/^1[3-9]\d{9}$/.test(formData.phone.trim())) { MessagePlugin.warning('请输入正确的手机号格式'); return; }
      if (formData.email?.trim() && !/^[\w.-]+@[\w-]+\.[\w.-]+$/.test(formData.email.trim())) { MessagePlugin.warning('请输入正确的邮箱格式'); return; }
      if (!formData.position?.trim()) { MessagePlugin.warning('请输入职位'); return; }
      if (!formData.company?.trim()) { MessagePlugin.warning('请输入公司'); return; }
      if (!formData.address?.trim()) { MessagePlugin.warning('请输入地址'); return; }

      // 新增时必须有userId
      if (!editingRecord && !formData.userId) {
        MessagePlugin.warning('请先通过手机号搜索并选择关联员工');
        return;
      }

      setSubmitting(true);

      const tenantInfo = tokenStorage.getTenantInfo();

      const payload: any = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        position: formData.position.trim(),
        email: formData.email?.trim() || '',
        company: formData.company.trim(),
        address: formData.address.trim(),
        status: formData.status,
        tenantId: tenantInfo?.id,
        tenantType: tenantInfo?.tenantType,
        fontColor: formData.fontColor,
        backgroundImage: formData.backgroundImage || '',
        introduction: normalizeRichText(formData.introduction),
      };

      let res: any;
      if (editingRecord?.id) {
        res = await tenantAdminBusinessCardApi.update(editingRecord.id, payload);
      } else {
        res = await tenantAdminBusinessCardApi.create(formData.userId!, payload);
      }

      if (res.code === 200) {
        MessagePlugin.success(editingRecord ? '更新成功' : '创建成功');
        setDialogVisible(false);
        setEditingRecord(null);
        setSelectedUser(null);
        setFormData(emptyForm);
        fetchData();
      } else {
        MessagePlugin.error(res.message || '保存失败');
      }
    } catch {
      MessagePlugin.error('保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo<PrimaryTableCol<any>[]>(() => [
    {
      colKey: 'name',
      title: '姓名',
      ellipsis: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-800 truncate">{row.name || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'company',
      title: '公司',
      ellipsis: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <span className="truncate">{row.company || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'position',
      title: '职位',
      ellipsis: true,
      cell: ({ row }) => (
        <span className="text-sm text-gray-700 truncate block">{row.position || '-'}</span>
      ),
    },
    {
      colKey: 'address',
      title: '地址',
      ellipsis: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <span className="truncate">{row.address || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'phone',
      title: '手机号',
      ellipsis: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <span className="truncate">{row.phone || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'email',
      title: '邮箱',
      ellipsis: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <span className="truncate">{row.email || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'createdAt',
      title: '创建时间',
      ellipsis: true,
      width: 140,
      cell: ({ row }) => (
        <span className="text-sm text-gray-500 truncate block">{row.createdAt || row.createTime || '-'}</span>
      ),
    },
    {
          title: '状态',
          colKey: 'userStatus',
          width: 100,
          cell: ({ row }: { row: any }) => (
            <Switch
              value={row.status === 1}
              onChange={(checked) => handleStatusChange(row, checked)}
              label={['启用', '禁用']}
            />
          ),
        },
    {
      colKey: 'action',
      title: '操作',
      width: 180,
      fixed: 'right',
      cell: ({ row }) => {
        return (
          <Space className="flex-wrap">
            <Button
              theme="primary"
              variant="text"
              size="small"
              onClick={() => openEdit({ ...row })}
            >
              编辑
            </Button>
            {row.status === 1 && (
              <Popconfirm content="确定停用该电子名片吗？" onConfirm={() => handleDisable(row.id)}>
                <Button theme="warning" variant="text" size="small">停用</Button>
              </Popconfirm>
            ) }
            <Popconfirm content="确定删除该电子名片吗？" onConfirm={() => handleDelete(row.id)}>
              <Button theme="danger" variant="text" size="small">删除</Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ], []);

  return (
    <div className="flex-1 w-full min-w-0 max-w-full overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-lg font-semibold text-gray-900 shrink-0">租户电子名片管理</h1>
        <div className="flex flex-wrap items-center gap-2 max-w-full">
          {/* {selectedRowKeys.length > 0 && (
            <Popconfirm content={`确定删除选中的 ${selectedRowKeys.length} 条数据吗？`} onConfirm={handleBatchDelete}>
              <Button theme="danger" variant="outline" icon={<Trash2 className="w-4 h-4" />}>
                批量删除
              </Button>
            </Popconfirm>
          )} */}
          <Button theme="primary" icon={<Plus className="w-4 h-4" />} onClick={openCreate}>
            新增名片
          </Button>
        </div>
      </div>

      {/* <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 whitespace-nowrap">搜索</label>
            <Input
              value={keyword}
              onChange={(val) => setKeyword(val)}
              placeholder="姓名 / 手机号 / 公司"
              clearable
              onClear={() => handleReset()}
              style={{ width: 220 }}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 whitespace-nowrap">状态</label>
            <Select
              value={status}
              onChange={(val) => setStatus(val as number | undefined)}
              clearable
              placeholder="全部"
              style={{ width: 120 }}
            >
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button theme="primary" icon={<Search className="w-4 h-4" />} onClick={handleSearch}>搜索</Button>
            <Button icon={<X className="w-4 h-4" />} onClick={handleReset}>重置</Button>
          </div>
        </div>
      </div> */}

      <div className="bg-white rounded-lg shadow-sm overflow-hidden min-w-0">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          data={data}
          selectedRowKeys={selectedRowKeys}
          onSelectChange={(keys: any) => setSelectedRowKeys(keys)}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showJumper: true,
            showPageSize: true,
            pageSizeOptions: [10, 20, 50],
            onChange: handlePageChange,
          }}
          bordered={false}
          hover
          size="small"
          tableLayout="fixed"
        />
      </div>

      {/* 新增 / 编辑弹框 */}
      <Dialog
        visible={dialogVisible}
        header={editingRecord ? '编辑电子名片' : '新增电子名片'}
        confirmBtn="保存"
        onClose={() => {
          setDialogVisible(false);
          setEditingRecord(null);
          setSelectedUser(null);
          setFormData(emptyForm);
        }}
        onConfirm={handleSubmit}
        confirmLoading={submitting}
        width={720}

      >
        <Form key={editingRecord ? 'edit' : 'create'} ref={formRef} initialData={formData} labelWidth={110} className="max-h-[65vh] overflow-y-auto pr-1">
          {/* 关联员工选择 - 仅新增时显示 */}
          {!editingRecord && (
            <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-100">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-500 mb-1">关联员工</div>
                  {selectedUser ? (
                    <div className="flex items-center gap-2 text-sm text-gray-800">
                      <UserCircle className="w-4 h-4 text-blue-500" />
                      <span className="font-medium">{selectedUser.name}</span>
                      <span className="text-gray-400">（{selectedUser.phone}）</span>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">未选择，请通过手机号搜索员工</div>
                  )}
                </div>
                <Button
                  size="small"
                  theme="primary"
                  variant="outline"
                  onClick={openUserPicker}
                >
                  选择员工
                </Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-1">
            <FormItem label="姓名" name="name" rules={[{ required: true, message: '请输入姓名' }]}>
              <Input value={formData.name} onChange={(val) => setFormData((prev) => ({ ...prev, name: val }))} placeholder="请输入姓名" />
            </FormItem>

            <FormItem label="手机号" name="phone" rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式' }
            ]}>
              <Input value={formData.phone} onChange={(val) => setFormData((prev) => ({ ...prev, phone: val }))} placeholder="请输入手机号" />
            </FormItem>

            <FormItem label="职位" name="position" rules={[{ required: true, message: '请输入职位' }]}>
              <Input value={formData.position} onChange={(val) => setFormData((prev) => ({ ...prev, position: val }))} placeholder="请输入职位" />
            </FormItem>

            <FormItem label="邮箱" name="email" rules={[
              { pattern: /^[\w.-]+@[\w-]+\.[\w.-]+$/, message: '请输入正确的邮箱格式' }
            ]}>
              <Input value={formData.email} onChange={(val) => setFormData((prev) => ({ ...prev, email: val }))} placeholder="请输入邮箱" />
            </FormItem>

            <FormItem label="公司" name="company" rules={[{ required: true, message: '请输入公司' }]}>
              <Input value={formData.company} onChange={(val) => setFormData((prev) => ({ ...prev, company: val }))} placeholder="请输入公司" />
            </FormItem>

            <FormItem label="地址" name="address" rules={[{ required: true, message: '请输入地址' }]}>
              <Input value={formData.address} onChange={(val) => setFormData((prev) => ({ ...prev, address: val }))} placeholder="请输入地址" />
            </FormItem>

            <FormItem label="个人简介" name="introduction">
              <div className="w-full [&_.ql-editor]:min-h-[160px]">
                <ProductRichTextEditor
                  value={formData.introduction}
                  onChange={(val) => setFormData((prev) => ({ ...prev, introduction: val }))}
                  showHeight={false}
                />
              </div>
            </FormItem>

            <FormItem label="背景图" name="backgroundImage">
              <ProductImageUploader
                value={formData.backgroundImage}
                onChange={(url) => setFormData((prev) => ({ ...prev, backgroundImage: url }))}
                simple
              />
            </FormItem>
            <FormItem label="字体颜色" name="fontColor">
              <ColorPickerField value={formData.fontColor} onChange={(v) => setFormData((prev) => ({ ...prev, fontColor: v }))} />
            </FormItem>
          </div>
        </Form>
      </Dialog>

      {/* 员工搜索选择弹框 */}
      <Dialog
        visible={userPickerVisible}
        header="选择关联员工（通过手机号搜索）"
        confirmBtn="确定"
        cancelBtn="取消"
        width={600}
        onClose={() => setUserPickerVisible(false)}
        onConfirm={() => {
          if (!selectedUser) {
            MessagePlugin.warning('请选择员工');
            return;
          }
          void confirmSelectUser(selectedUser);
        }}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Input
              value={userSearchPhone}
              onChange={(val) => setUserSearchPhone(val)}
              placeholder="请输入手机号搜索"
              clearable
            />
            <Button theme="primary" icon={<Search className="w-4 h-4" />} onClick={doSearchUsers} loading={userListLoading}>
              搜索
            </Button>
          </div>

          <div className="max-h-[400px] overflow-y-auto border border-gray-100 rounded-lg">
            {userList.length === 0 ? (
              <div className="py-12 text-center text-gray-400 text-sm">
                {userListLoading ? '搜索中...' : '请输入手机号搜索员工'}
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {userList.map((user) => {
                  const isActive = selectedUser?.userId === user.userId;
                  return (
                    <div
                      key={user.userId}
                      onClick={() => setSelectedUser(user)}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                        isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                        <UserCircle className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-800">{user.name}</span>
                          <Tag theme="primary" variant="light" size="small" shape="square">{user.phone}</Tag>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate">
                          {[user.position, user.company].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      {isActive && (
                        <div className="text-blue-500 text-sm font-medium">已选</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}
